import React, { useState, useEffect } from "react";
import API from "../services/api";
import { printNote } from "./PrintPDFUtils";

/* ===============================
   MODAL: TA CONTRACT
   =============================== */
function TAContractModal({ isOpen, onClose, onSubmit, clientId, clientName }) {
  if (!isOpen) return null;

  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [responses, setResponses] = useState({
    endVision: "",
    currentState: "",
    desiredState: "",
    feelings: "",
    selfBeliefs: "",
    otherBeliefs: "",
    actions: "",
    enhanceLife: "",
    howToAccomplish: "",
    benefit: "",
    measureSuccess: "",
    sabotage: "",
    avoidSabotage: "",
    loss: "",
    gain: ""
  });

  const handleChange = (key, value) => {
    setResponses(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    const taNote = {
      date,
      type: "TA Contract",
      ...responses
    };
    onSubmit(taNote);
  };

  // Questions organized in an array for easier rendering
  const questions = [
    ["endVision", "Imagine it's the end of therapy… how are you different?"],
    ["currentState", "Currently:"],
    ["desiredState", "How you would like to be:"],
    ["feelings", "What I am feeling:"],
    ["selfBeliefs", "Beliefs about myself:"],
    ["otherBeliefs", "Beliefs about others:"],
    ["actions", "What I am doing:"],
    ["enhanceLife", "What could you change about yourself that would enhance your life?"],
    ["howToAccomplish", "What would you need to do to accomplish that?"],
    ["benefit", "What about that would be beneficial or pleasing to you?"],
    ["measureSuccess", "How would you and I both know that you've gotten there?"],
    ["sabotage", "How could you sabotage your success?"],
    ["avoidSabotage", "What will you do instead to ensure success?"],
    ["loss", "What will you be letting go of when you change?"],
    ["gain", "What will you be gaining?"]
  ];

  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalHeader}>TA Contract</h2>
        
        <div style={styles.clientInfo}>
          <strong>Client:</strong> {clientName || "Selected Client"} | <strong>Date:</strong> {date}
          <button 
            onClick={() => setDate(new Date().toISOString().split("T")[0])}
            style={styles.smallButton}
          >
            Set Today
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={styles.dateInput}
          />
        </div>

        <div style={styles.formScrollArea}>
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Transactional Analysis Contract</h3>
            <p style={styles.description}>
              This form documents the client's goals and commitments in therapy from a Transactional Analysis perspective.
            </p>
          </div>

          {/* TA Contract Questions */}
          {questions.map(([key, label], index) => (
            <div key={key} style={styles.formSection}>
              <h3 style={styles.sectionHeader}>{label}</h3>
              <textarea
                rows="3"
                value={responses[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                style={styles.textArea}
                placeholder={`Client's response to "${label}"`}
              />
            </div>
          ))}
        </div>

        <div style={styles.modalButtons}>
          <button onClick={handleSave} style={styles.saveBtn}>Save</button>
          <button onClick={onClose} style={styles.cancelBtn}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ===============================
   MODAL: TA Contract View Modal
   =============================== */
function TAContractViewModal({ isOpen, note, onClose, onUpdate, onSendAsPDF }) {
  if (!isOpen || !note) return null;

  let parsed = {};
  try {
    parsed = JSON.parse(note.notes);
  } catch (err) {
    console.error("Error parsing note:", err);
  }

  const [editMode, setEditMode] = useState(false);
  const [date, setDate] = useState(parsed.date || "");
  const [form, setForm] = useState(parsed);
  
  // Store client name from note for printing
  const [clientName, setClientName] = useState("");
  
  // Fetch client name on load
  useEffect(() => {
    if (note && note.client_id) {
      const fetchClientName = async () => {
        try {
          const res = await API.get(`/therapist/clients/${note.client_id}`);
          if (res.data && res.data.client_name) {
            setClientName(res.data.client_name);
          }
        } catch (err) {
          console.error("Error fetching client name:", err);
        }
      };
      
      fetchClientName();
    }
  }, [note]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    const updatedData = {
      ...form,
      date
    };
    onUpdate(note.id, updatedData);
    setEditMode(false);
  };
  
  // Handle printing
  const handlePrint = () => {
    const noteData = {
      ...form,
      date
    };
    
    printNote({notes: JSON.stringify(noteData)}, "TA Contract", clientName);
  };

  // Questions organized in an array for easier rendering
  const fields = [
    ["endVision", "Imagine it's the end of therapy… how are you different?"],
    ["currentState", "Currently:"],
    ["desiredState", "How you would like to be:"],
    ["feelings", "What I am feeling:"],
    ["selfBeliefs", "Beliefs about myself:"],
    ["otherBeliefs", "Beliefs about others:"],
    ["actions", "What I am doing:"],
    ["enhanceLife", "What could you change about yourself that would enhance your life?"],
    ["howToAccomplish", "What would you need to do to accomplish that?"],
    ["benefit", "What about that would be beneficial or pleasing to you?"],
    ["measureSuccess", "How would you and I both know that you've gotten there?"],
    ["sabotage", "How could you sabotage your success?"],
    ["avoidSabotage", "What will you do instead to ensure success?"],
    ["loss", "What will you be letting go of when you change?"],
    ["gain", "What will you be gaining?"]
  ];

  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalHeader}>
          {editMode ? "Edit TA Contract" : "View TA Contract"}
        </h2>

        <div style={{ textAlign: "right", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          {!editMode && (
            <>
              <button
                onClick={handlePrint}
                style={{ ...styles.button, backgroundColor: "var(--text-muted)" }}
              >
                Print/PDF
              </button>
              <button 
                onClick={() => setEditMode(true)} 
                style={{ ...styles.button, backgroundColor: "#ffc107" }}
              >
                Edit
              </button>
            </>
          )}
          {onSendAsPDF && (
            <button onClick={() => onSendAsPDF(note)} style={{ ...styles.button, backgroundColor: "var(--primary-color)" }}>
              ✉️ Send as PDF
            </button>
          )}
          <button onClick={onClose} style={styles.cancelBtn}>Close</button>
        </div>

        <div style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: "1rem" }}>
          Created: {new Date(note.created_at).toLocaleString()}
        </div>

        <div style={styles.formScrollArea}>
          {/* Date */}
          <div style={styles.formSection}>
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Date:</label>
              {editMode ? (
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={styles.input}
                />
              ) : (
                <div style={styles.readOnly}>{date}</div>
              )}
            </div>
          </div>
          
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Transactional Analysis Contract</h3>
            <p style={styles.description}>
              This form documents the client's goals and commitments in therapy from a Transactional Analysis perspective.
            </p>
          </div>

          {fields.map(([key, label]) => (
            <div key={key} style={styles.formSection}>
              <h3 style={styles.sectionHeader}>{label}</h3>
              {editMode ? (
                <textarea
                  rows="3"
                  value={form[key] || ""}
                  onChange={(e) => handleChange(key, e.target.value)}
                  style={styles.textArea}
                />
              ) : (
                <div style={styles.readOnly}>{form[key]}</div>
              )}
            </div>
          ))}
        </div>

        {editMode && (
          <div style={styles.modalButtons}>
            <button onClick={handleSave} style={styles.saveBtn}>Save Changes</button>
            <button 
              onClick={() => setEditMode(false)} 
              style={styles.cancelBtn}
            >
              Cancel Edits
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Styles for the modals
const styles = {
  modalBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: "1rem"
  },
  modalContent: {
    backgroundColor: "#fff",
    padding: "1rem",
    borderRadius: "6px",
    maxHeight: "90vh",
    overflowY: "auto",
    width: "700px",
    maxWidth: "94vw",
    boxSizing: "border-box",
    position: "relative"
  },
  modalHeader: {
    fontSize: "1.25rem",
    margin: 0,
    marginBottom: "0.5rem"
  },
  clientInfo: {
    marginBottom: "1rem",
    padding: "0.5rem",
    backgroundColor: "var(--surface-muted)",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    gap: "1rem"
  },
  formScrollArea: {
    maxHeight: "60vh",
    overflowY: "auto",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px"
  },
  formSection: {
    marginBottom: "1.5rem",
    padding: "0.5rem",
    backgroundColor: "var(--surface-muted)",
    borderRadius: "4px",
    border: "1px solid var(--border-color)"
  },
  description: {
    color: "#495057",
    fontStyle: "italic",
    marginBottom: "0.5rem"
  },
  sectionHeader: {
    fontSize: "1rem",
    fontWeight: "bold",
    margin: "0 0 0.75rem 0",
    color: "var(--text-strong)",
    borderBottom: "1px solid var(--border-color)",
    paddingBottom: "0.25rem"
  },
  textArea: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    resize: "vertical",
    fontFamily: "inherit",
    fontSize: "0.9rem",
    boxSizing: "border-box"
  },
  input: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    fontSize: "0.9rem",
    boxSizing: "border-box"
  },
  smallButton: {
    padding: "0.25rem 0.5rem",
    fontSize: "0.8rem",
    backgroundColor: "var(--text-muted)",
    color: "#fff",
    border: "none",
    borderRadius: "3px",
    cursor: "pointer"
  },
  dateInput: {
    padding: "0.25rem",
    border: "1px solid var(--border-color)",
    borderRadius: "3px",
    marginLeft: "0.5rem"
  },
  readOnly: {
    padding: "0.5rem",
    backgroundColor: "var(--surface-muted)",
    borderRadius: "4px",
    minHeight: "1rem"
  },
  formField: {
    marginBottom: "0.75rem"
  },
  fieldLabel: {
    fontWeight: "bold",
    marginBottom: "0.25rem",
    display: "block"
  },
  modalButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.5rem",
    marginTop: "1rem"
  },
  saveBtn: {
    backgroundColor: "var(--success-color)",
    border: "none",
    padding: "0.5rem 1rem",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer"
  },
  cancelBtn: {
    backgroundColor: "var(--text-strong)",
    border: "none",
    padding: "0.5rem 1rem",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer"
  },
  button: {
    backgroundColor: "var(--primary-color)",
    border: "none",
    color: "#fff",
    padding: "0.4rem 0.75rem",
    borderRadius: "4px",
    cursor: "pointer"
  }
};

export { TAContractModal, TAContractViewModal };